import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  FormGroup,
  Stack,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Typography,
  Button,
  SelectChangeEvent,
  Tooltip,
  IconButton,
  Box,
} from '@mui/material'
import {
  DashBoardConfigure,
  DashBoardItemData,
  DashBoardItemOption,
  GraphItemType,
} from 'features/dashBoard/types'

import { CloseIcon, OkIcon } from 'shared/icons'

import { GraphOptionLine } from './components'
import { TargetListResult } from "interface/search/targetList"

type EditDashBoardItemProps<T> = {
  readonly isCreate?: boolean
  readonly configure: DashBoardConfigure<T>
  readonly itemData: DashBoardItemData
  targetList?: TargetListResult
  onSubmitForm: (itemData: DashBoardItemData) => void
  onClose: () => void
}
export const EditDashBoardItem = <T,>({
  isCreate,
  configure,
  targetList,
  itemData,
  onSubmitForm,
  onClose
}: EditDashBoardItemProps<T>) => {
	const firstFocusInputRef = useRef<HTMLInputElement>(null)
  const [title, setTitle] = useState<string>(itemData.title ?? '')
  const [filterText, setFilterText] = useState<string>(itemData.filterText ?? '')

  const [graphType, setGraphType] = useState<GraphItemType>(itemData.graphType ?? 'horizonBar')
  const [options, setOptions] = useState<DashBoardItemOption[]>(itemData.options || [])

  const graphDef = useMemo(
    () => configure.graphs[graphType],
    [configure, graphType]
  )

  const setOptionValue = useCallback(
    (id: string, value: string) => {
      const newOptions = options.map((opt) => ({ ...opt }))
      const it = newOptions.find((opt) => opt.id === id)
      if (!it) {
        newOptions.push({ id: id, value: value })
      } else {
        it.value = value
      }
      setOptions(newOptions)
    },
    [options, setOptions]
  )
  const handleOnGraphType = useCallback(
    (event: SelectChangeEvent<GraphItemType>) =>
      setGraphType(event.target.value as GraphItemType),
    []
  )
	useEffect(
		() => {
			const time = setTimeout(() => firstFocusInputRef.current?.focus(), 0)
			return () => clearTimeout(time)
		},
		[firstFocusInputRef]
	)
  return (
    <>
      <FormGroup>
        <Stack direction="column">
          <Stack direction='row' padding={1} spacing={1} alignItems='center'>
            <Typography variant="body1" flexGrow={1} whiteSpace='nowrap' overflow='hidden' textOverflow='ellipsis'>
              {isCreate ? '新しいグラフを追加' : 'グラフの編集'}
            </Typography>
            <Tooltip title="閉じる">
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack direction="column" spacing={2} paddingX={2}>
            <TextField
              ref={firstFocusInputRef}
              margin="dense"
              id="title"
              label="タイトル（省略可）"
              fullWidth
              variant="outlined"
              size="small"
              defaultValue={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />

            <FormControl>
              <InputLabel id="graph-type-label">グラフ種類</InputLabel>
              <Select
                labelId="graph-type-label"
                label="グラフ種類"
                id="graph-type"
                value={graphType}
                size="small"
                onChange={handleOnGraphType}
              >
                {Object.keys(configure.graphs).map((graphType) => {
                  const def = configure.graphs[graphType]
                  return (
                    <MenuItem key={`graph-${graphType}`} value={graphType}>
                      {def.title}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>

            { graphDef.options?.map((line, i) => (
              <GraphOptionLine
                key={`optionline-${i}`}
                targetList={targetList}
                line={line}
                options={options}
                configure={configure}
                graphType={graphType}
                setOptionValue={setOptionValue}
              />
            )) }

            <TextField
              margin="dense"
              id="filterText"
              label="絞り込み"
              fullWidth
              variant="outlined"
              size="small"
              defaultValue={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </Stack>

          <Stack direction="row" spacing={1} padding={2}>
            <Box flexGrow={1}/>
            <Stack flexShrink={0} direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<OkIcon/>}
                onClick={() => {
                  const saveOptions = graphDef.options?.flatMap((line) =>
                    line.map((option) => {
                      const defaultValue = (
                        option.type === 'axis' ? Object.keys(configure.axis)[0] :
                        option.type === 'value' ? Object.keys(configure.value)[0] :
                        option.default
                      ) ?? ''
                      return {
                        id: option.id,
                        value:
                          options.find((opt) => opt.id === option.id)?.value ??
                          (option.nullable ? '**' : defaultValue),
                      } as DashBoardItemOption
                    })
                  )
                  .filter((option) => option.value !== '**')

                  onSubmitForm({
                    ...itemData,
                    title: title || undefined,
                    graphType: graphType,
                    options: saveOptions,
                    filterText: filterText || undefined
                  })
                }}
              >
                {isCreate ? '登 録' : '適 用'}
              </Button>
              <Button variant="text" onClick={onClose}>
                キャンセル
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </FormGroup>
    </>
  )
}
